import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/common/UI";
import { StatusBadge, RoleBadge } from "@/components/common/Badges";
import { fmtDateTime } from "@/lib/format";
import { Mail, MailOpen, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";

export default function NotificationsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/notifications", { params: { all } }).then((r) => setData(r.data));
  useEffect(() => { setData(null); load(); }, [all]); // eslint-disable-line

  const markAll = async () => { await api.post("/notifications/read", {}); toast.success("All marked as read"); load(); window.dispatchEvent(new Event("notif-refresh")); };
  const openItem = async (n) => {
    setOpen(n.id === open ? null : n.id);
    if (!n.read && n.user_id === user.id) { await api.post("/notifications/read", { ids: [n.id] }); load(); window.dispatchEvent(new Event("notif-refresh")); }
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="Email outbox — messages generated when orders are approved, rejected or shipped">
        {user.role === "admin" && <label className="flex items-center gap-2 text-sm font-medium"><input data-testid="notif-show-all" type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="h-4 w-4 accent-red-600" />Show all recipients (admin)</label>}
        <button data-testid="notif-mark-all" onClick={markAll} className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold hover:bg-slate-50"><CheckCheck className="h-4 w-4" />Mark all read</button>
      </PageHeader>
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800" data-testid="notif-outbox-banner">Email delivery is in <strong>outbox mode</strong> — messages are stored here instead of being sent. Connect a provider (e.g. Resend) to deliver them to inboxes.</div>
      {!data ? <Spinner /> : data.items.length === 0 ? <EmptyState title="No notifications yet" hint="Approve, reject or ship an order to generate one" /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100" data-testid="notif-list">
          {data.items.map((n) => (
            <div key={n.id} data-testid={`notif-${n.id}`} className={cn("transition-colors", !n.read && n.user_id === user.id && "bg-red-50/40")}>
              <button onClick={() => openItem(n)} className="flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-slate-50">
                <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", n.read ? "bg-slate-100 text-slate-400" : "bg-red-600 text-white")}>{n.read ? <MailOpen className="h-4 w-4" /> : <Mail className="h-4 w-4" />}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2"><span className={cn("truncate text-sm", n.read ? "font-medium text-slate-700" : "font-bold text-slate-900")}>{n.subject}</span><StatusBadge status={n.event} /></span>
                  <span className="mt-0.5 block text-xs text-slate-500">To: {n.to_name} &lt;{n.to}&gt; <RoleBadge role={n.to_role} /> · {fmtDateTime(n.created_at)}</span>
                </span>
              </button>
              {open === n.id && <div className="border-t border-slate-100 bg-slate-50 px-5 py-4"><pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">{n.body}</pre><button data-testid={`notif-open-order-${n.order_number}`} onClick={() => nav(`/orders/${n.order_id}`)} className="mt-3 text-sm font-semibold text-red-600 hover:underline">Open order {n.order_number} →</button></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
